/**
 * Keeps conversational text in Meridian message bubbles while rendering generative UI
 * as independent full-width content so forms and controls retain valid semantics.
 */
import React from 'react';
import { type UIMessage } from 'ai';
import Column from '@amzn/meridian/column';
import Message from '@amzn/meridian/message';
import Text from '@amzn/meridian/text';

import { renderMessage } from 'src/components/chat';
import { type ChatMessageProps as RendererProps } from 'src/components/chat/types';

interface IndexedPart {
  index: number;
  part: UIMessage['parts'][number];
}

interface ChatMessageProps {
  agentName: string;
  isLastMessage: boolean;
  isLoading: boolean;
  message: UIMessage;
  nextMessage?: UIMessage;
  userName: string;
  onSend: (text: string) => void;
}

interface RenderPartOptions extends Omit<RendererProps, 'index' | 'part'> {
  indexedPart: IndexedPart;
}

const renderPart = ({ indexedPart, isLastMessage, isLoading, isUserMessage, nextMessage, onSend }: RenderPartOptions): React.ReactNode => (
  renderMessage({
    index: indexedPart.index,
    isLastMessage,
    isLoading,
    isUserMessage,
    ...(nextMessage ? { nextMessage } : {}),
    onSend,
    part: indexedPart.part as unknown as Record<string, unknown>,
  })
);

export const ChatMessage: React.FC<ChatMessageProps> = ({ agentName, isLastMessage, isLoading, message, nextMessage, userName, onSend }) => {
  const isUserMessage = message.role === 'user';
  const indexedParts = message.parts.map((part, index) => ({ index, part }));
  const textParts = indexedParts.filter(({ part }) => part.type === 'text' && !part.text.startsWith('[Context:'));
  const blockParts = indexedParts.filter(({ part }) => part.type !== 'text');
  const hasNonEmptyText = textParts.some(({ part }) => part.type === 'text' && part.text.trim().length > 0);
  const rendererProps = { isLastMessage, isLoading, isUserMessage, nextMessage, onSend };

  if (!hasNonEmptyText && blockParts.length === 0) return null;

  return (
    <Column spacing="300" width="100%">
      {hasNonEmptyText && (
        <Message
          data-testid={`chat-message-text-${message.id}`}
          maxWidth="300px"
          name={isUserMessage ? userName : agentName}
          showAvatar={false}
          size="medium"
          type={isUserMessage ? 'outgoing' : 'incoming'}
        >
          <Text breakWord color="primary" tag="div" type="b300">
            {textParts.map(indexedPart => (
              <React.Fragment key={indexedPart.index}>
                {renderPart({ ...rendererProps, indexedPart })}
              </React.Fragment>
            ))}
          </Text>
        </Message>
      )}
      {blockParts.length > 0 && (
        <Column alignmentHorizontal="start" data-testid={`chat-message-blocks-${message.id}`} spacing="300" width="100%">
          {blockParts.map(indexedPart => (
            <React.Fragment key={indexedPart.index}>
              {renderPart({ ...rendererProps, indexedPart })}
            </React.Fragment>
          ))}
        </Column>
      )}
    </Column>
  );
};
